import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import { Job, Invitation } from '../../types';
import {
  EXTERNAL_TEMPLATE_CHANNELS,
  MESSAGING_EVENTS,
  TEMPLATE_VARIABLES,
  MessagingEventKey,
} from '../../lib/messagingConstants';

type TemplateRow = {
  id: string;
  organizationId: string;
  eventKey: string;
  channel: string;
  subject?: string | null;
  body: string;
  isActive: boolean;
};

type VariantRow = {
  id: string;
  name: string;
  eventKey: string;
  channel: string;
  subject?: string | null;
  body: string;
};

type BuiltInDefaults = Record<
  string,
  Partial<Record<'WHATSAPP' | 'EMAIL' | 'SMS', { subject?: string; body: string }>>
>;

type TemplateSource = 'built_in' | 'org_default' | `variant:${string}`;

interface MessagingTemplatesPageProps {
  isAdminView?: boolean;
}

export default function MessagingTemplatesPage({ isAdminView = false }: MessagingTemplatesPageProps) {
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const [agencyOrgs, setAgencyOrgs] = useState<{ id: string; name: string }[]>([]);
  /** Resolved agency org for API calls; hidden for single-agency users. */
  const [resolvedOrgId, setResolvedOrgId] = useState('');
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);
  const [builtIn, setBuiltIn] = useState<BuiltInDefaults | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [templateEvent, setTemplateEvent] = useState<MessagingEventKey>('INVITATION_SENT');
  const [templateSource, setTemplateSource] = useState<TemplateSource>('built_in');
  const [forms, setForms] = useState<Record<string, { subject: string; body: string }>>({});

  const [jobs, setJobs] = useState<Job[]>([]);
  const [previewJobId, setPreviewJobId] = useState('');
  const [previewInvId, setPreviewInvId] = useState('');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [previewChannel, setPreviewChannel] = useState<'WHATSAPP' | 'EMAIL' | 'SMS'>('EMAIL');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [previewOut, setPreviewOut] = useState<{ subjectRendered: string; bodyRendered: string } | null>(
    null
  );
  const [variantName, setVariantName] = useState('');

  const loadOrgs = useCallback(async () => {
    if (isAdminView) {
      const res = await api.get<{ items: { id: string; name: string; type: string }[] }>(
        '/admin/organizations',
        { params: { type: 'AGENCY', limit: 200 } }
      );
      const items = res.data.items || [];
      setAgencyOrgs(items.map((o) => ({ id: o.id, name: o.name })));
      return;
    }
    const memberships = user?.memberships?.filter((m) => m.organization?.type === 'AGENCY') || [];
    const orgs = memberships.map((m) => ({
      id: m.organizationId,
      name: m.organization?.name || m.organizationId,
    }));
    setAgencyOrgs(orgs);
    if (orgs.length === 1) {
      setResolvedOrgId(orgs[0].id);
    }
  }, [isAdminView, user?.memberships]);

  const loadBuiltIn = useCallback(async () => {
    const res = await api.get<BuiltInDefaults>('/messaging-templates/built-in-defaults');
    setBuiltIn(res.data);
  }, []);

  const loadAll = useCallback(async () => {
    if (!resolvedOrgId) return;
    setLoading(true);
    try {
      const tplRes = await api.get<{ templates: TemplateRow[]; variants: VariantRow[] }>(
        '/messaging-templates',
        {
          params: { organizationId: resolvedOrgId, includeVariants: '1' },
        }
      );
      setTemplates(tplRes.data.templates || []);
      setVariants(tplRes.data.variants || []);
    } catch {
      setTemplates([]);
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, [resolvedOrgId]);

  const loadJobs = useCallback(async () => {
    if (!resolvedOrgId) {
      setJobs([]);
      return;
    }
    try {
      const res = await api.get<Job[]>('/jobs', { params: { managedByOrgId: resolvedOrgId } });
      setJobs(res.data);
    } catch {
      setJobs([]);
    }
  }, [resolvedOrgId]);

  useEffect(() => {
    void loadOrgs();
  }, [loadOrgs]);

  useEffect(() => {
    void loadBuiltIn();
  }, [loadBuiltIn]);

  useEffect(() => {
    const ev = searchParams.get('event');
    const ch = searchParams.get('channel');
    if (ev && MESSAGING_EVENTS.some((e) => e.key === ev)) {
      setTemplateEvent(ev as MessagingEventKey);
    }
    if (ch === 'WHATSAPP' || ch === 'EMAIL' || ch === 'SMS') {
      setPreviewChannel(ch);
    }
  }, [searchParams]);

  useEffect(() => {
    if (resolvedOrgId) {
      void loadAll();
      void loadJobs();
    }
  }, [resolvedOrgId, loadAll, loadJobs]);

  useEffect(() => {
    setPreviewJobId('');
    setPreviewInvId('');
    setInvitations([]);
  }, [resolvedOrgId]);

  useEffect(() => {
    if (!previewJobId) {
      setInvitations([]);
      setPreviewInvId('');
      return;
    }
    void api
      .get<Invitation[]>('/invitations', { params: { jobId: previewJobId } })
      .then((r) => setInvitations(r.data))
      .catch(() => setInvitations([]));
  }, [previewJobId]);

  const orgRowForChannel = (ch: 'WHATSAPP' | 'EMAIL' | 'SMS') =>
    templates.find((t) => t.eventKey === templateEvent && t.channel === ch);

  const applySourceToForms = useCallback(
    (source: TemplateSource, event: MessagingEventKey) => {
      for (const ch of EXTERNAL_TEMPLATE_CHANNELS) {
        const c = ch as 'WHATSAPP' | 'EMAIL' | 'SMS';
        let subject = '';
        let body = '';
        if (source === 'built_in') {
          const d = builtIn?.[event]?.[c];
          subject = d && 'subject' in d && d.subject ? d.subject : '';
          body = d?.body || '';
        } else if (source === 'org_default') {
          const row = templates.find((t) => t.eventKey === event && t.channel === c);
          subject = row?.subject || '';
          body = row?.body || '';
        } else if (source.startsWith('variant:')) {
          const vid = source.slice('variant:'.length);
          const v = variants.find((x) => x.id === vid && x.eventKey === event);
          if (v && v.channel === c) {
            subject = v.subject || '';
            body = v.body || '';
          } else {
            const row = templates.find((t) => t.eventKey === event && t.channel === c);
            subject = row?.subject || '';
            body = row?.body || '';
            if (!body) {
              const d = builtIn?.[event]?.[c];
              subject = d && 'subject' in d && d.subject ? d.subject : '';
              body = d?.body || '';
            }
          }
        }
        setForms((prev) => ({
          ...prev,
          [c]: { subject, body },
        }));
      }
    },
    [builtIn, templates, variants]
  );

  useEffect(() => {
    if (!builtIn) return;
    applySourceToForms(templateSource, templateEvent);
  }, [templateEvent, templateSource, builtIn, templates, variants, applySourceToForms]);

  useEffect(() => {
    setPreviewSubject(forms[previewChannel]?.subject || '');
    setPreviewBody(forms[previewChannel]?.body || '');
  }, [templateEvent, previewChannel, forms, templateSource]);

  const templateSourceOptions = useMemo(() => {
    const opts: { value: TemplateSource; label: string }[] = [
      { value: 'built_in', label: 'Platform default (not saved)' },
      { value: 'org_default', label: 'Agency default (saved in DB)' },
    ];
    for (const v of variants.filter((x) => x.eventKey === templateEvent)) {
      opts.push({ value: `variant:${v.id}` as TemplateSource, label: `Variant: ${v.name}` });
    }
    return opts;
  }, [variants, templateEvent]);

  const runPreview = async () => {
    if (!resolvedOrgId || !previewJobId) return;
    const bodyForApi =
      templateSource === 'built_in'
        ? forms[previewChannel]?.body || ''
        : previewBody;
    const subjForApi =
      previewChannel === 'EMAIL'
        ? templateSource === 'built_in'
          ? forms.EMAIL?.subject || ''
          : previewSubject
        : undefined;

    const res = await api.post<{
      subjectRendered: string;
      bodyRendered: string;
    }>('/messaging-templates/preview', {
      organizationId: resolvedOrgId,
      jobId: previewJobId,
      invitationId: previewInvId || undefined,
      eventKey: templateEvent,
      channel: previewChannel,
      subject: subjForApi,
      body: bodyForApi,
    });
    setPreviewOut(res.data);
  };

  const saveOrgDefaultsForEvent = async () => {
    if (!resolvedOrgId) return;
    setSaving(true);
    try {
      const payload = EXTERNAL_TEMPLATE_CHANNELS.map((channel) => ({
        eventKey: templateEvent,
        channel,
        subject: channel === 'EMAIL' ? forms[channel]?.subject || '' : undefined,
        body: forms[channel]?.body || '',
        isActive: true,
      })).filter((t) => t.body.trim().length > 0);
      const res = await api.put<TemplateRow[]>(
        `/messaging-templates/${encodeURIComponent(resolvedOrgId)}`,
        { templates: payload }
      );
      setTemplates((prev) => {
        const rest = prev.filter((x) => x.eventKey !== templateEvent);
        return [...rest, ...res.data];
      });
      setTemplateSource('org_default');
    } finally {
      setSaving(false);
    }
  };

  const saveAsVariant = async () => {
    if (!resolvedOrgId || !variantName.trim()) return;
    const base = orgRowForChannel(previewChannel);
    await api.post(`/messaging-templates/${encodeURIComponent(resolvedOrgId)}/variants`, {
      name: variantName.trim(),
      eventKey: templateEvent,
      channel: previewChannel,
      subject: previewChannel === 'EMAIL' ? previewSubject : null,
      body: previewBody,
      baseTemplateId: base?.id,
    });
    setVariantName('');
    const tplRes = await api.get<{ templates: TemplateRow[]; variants: VariantRow[] }>(
      '/messaging-templates',
      { params: { organizationId: resolvedOrgId, includeVariants: '1' } }
    );
    setTemplates(tplRes.data.templates || []);
    setVariants(tplRes.data.variants || []);
  };

  const variantOptionsForChannel = useMemo(
    () => variants.filter((v) => v.eventKey === templateEvent && v.channel === previewChannel),
    [variants, templateEvent, previewChannel]
  );

  const showAdminOrgPicker = isAdminView && agencyOrgs.length > 0;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">
          Messaging templates
        </h1>
        <p className="text-glass-text-2 text-sm mt-1">
          <strong>Platform defaults</strong> are hardcoded on the server — nothing is stored until you
          click <strong>Save agency defaults</strong>. Then we persist rows for your agency; you can
          also save <strong>named variants</strong> from the preview editor.
        </p>
        <p className="text-glass-text-2 text-xs mt-2">
          <strong>Where to test:</strong> Job notifications —{' '}
          <code className="text-accent2">/agency/campaigns/&lt;slug&gt;</code> → <strong>Notification</strong>{' '}
          tab. WhatsApp setup: <code className="text-accent2">/agency/messaging</code>. Log:{' '}
          <code className="text-accent2">/agency/messaging-log</code>.
        </p>
      </div>

      {showAdminOrgPicker && (
        <Card>
          <CardHeader>
            <CardTitle>Agency (admin)</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={resolvedOrgId}
              onChange={(e) => setResolvedOrgId(e.target.value)}
              className="max-w-xl"
            >
              <option value="">Select agency…</option>
              {agencyOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </CardContent>
        </Card>
      )}

      {!isAdminView && !resolvedOrgId && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          No agency membership found. Join an agency org to edit templates.
        </p>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle>Template &amp; preview</CardTitle>
          {resolvedOrgId && (
            <Button type="button" variant="outline" size="sm" onClick={() => void loadAll()}>
              Refresh
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {loading && resolvedOrgId ? (
            <p className="text-sm text-glass-text-2">Loading…</p>
          ) : !resolvedOrgId && !isAdminView ? null : !resolvedOrgId && isAdminView ? (
            <p className="text-sm text-glass-text-2">Select an agency above.</p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs text-glass-text-2 block mb-1">1. Message template</label>
                  <Select
                    value={templateSource}
                    onChange={(e) => {
                      const v = e.target.value as TemplateSource;
                      setTemplateSource(v);
                      applySourceToForms(v, templateEvent);
                      setPreviewOut(null);
                    }}
                    className="max-w-xl"
                  >
                    {templateSourceOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-glass-text-2 block mb-1">Workflow event</label>
                  <Select
                    value={templateEvent}
                    onChange={(e) => {
                      const ev = e.target.value as MessagingEventKey;
                      setTemplateEvent(ev);
                      applySourceToForms(templateSource, ev);
                      setPreviewOut(null);
                    }}
                    className="max-w-xl"
                  >
                    {MESSAGING_EVENTS.map((ev) => (
                      <option key={ev.key} value={ev.key}>
                        {ev.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <p className="text-xs text-glass-text-2">
                Variables: {TEMPLATE_VARIABLES.join(' · ')}
              </p>

              {EXTERNAL_TEMPLATE_CHANNELS.map((ch) => (
                <div key={ch} className="border border-glass rounded-xl p-3 space-y-2">
                  <div className="font-medium text-sm text-glass-text">{ch}</div>
                  {ch === 'EMAIL' && (
                    <div>
                      <label className="text-xs text-glass-text-2">Subject</label>
                      <Input
                        value={forms[ch]?.subject || ''}
                        onChange={(e) =>
                          setForms((prev) => ({
                            ...prev,
                            [ch]: { ...prev[ch], subject: e.target.value, body: prev[ch]?.body || '' },
                          }))
                        }
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-glass-text-2">Body</label>
                    <Textarea
                      rows={ch === 'WHATSAPP' ? 3 : ch === 'SMS' ? 2 : 6}
                      value={forms[ch]?.body || ''}
                      onChange={(e) =>
                        setForms((prev) => ({
                          ...prev,
                          [ch]: { subject: prev[ch]?.subject || '', body: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>
              ))}

              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={saveOrgDefaultsForEvent} disabled={saving}>
                  {saving ? 'Saving…' : 'Save agency defaults for this event'}
                </Button>
                {templateSource === 'built_in' && (
                  <span className="text-xs text-glass-text-2 self-center">
                    You are editing from platform defaults — saving writes agency rows for all three channels.
                  </span>
                )}
              </div>

              <div className="border-t border-glass pt-4 space-y-3">
                <p className="text-sm font-medium text-glass-text">2. Preview with campaign &amp; KOL</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="text-xs text-glass-text-2">Campaign</label>
                    <Select value={previewJobId} onChange={(e) => setPreviewJobId(e.target.value)}>
                      <option value="">Select job…</option>
                      {jobs.map((j) => (
                        <option key={j.id} value={j.id}>
                          {j.title}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs text-glass-text-2">KOL (optional)</label>
                    <Select
                      value={previewInvId}
                      onChange={(e) => setPreviewInvId(e.target.value)}
                      disabled={!previewJobId}
                    >
                      <option value="">—</option>
                      {invitations.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {inv.kolProfile?.name || 'KOL'}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-glass-text-2">Channel</label>
                    <Select
                      value={previewChannel}
                      onChange={(e) => setPreviewChannel(e.target.value as typeof previewChannel)}
                    >
                      {EXTERNAL_TEMPLATE_CHANNELS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                {previewChannel === 'EMAIL' && (
                  <div>
                    <label className="text-xs text-glass-text-2">Subject (preview)</label>
                    <Input value={previewSubject} onChange={(e) => setPreviewSubject(e.target.value)} />
                  </div>
                )}
                <div>
                  <label className="text-xs text-glass-text-2">Body (preview)</label>
                  <Textarea rows={5} value={previewBody} onChange={(e) => setPreviewBody(e.target.value)} />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={runPreview}
                  disabled={!resolvedOrgId || !previewJobId}
                >
                  Preview
                </Button>
                {previewOut && (
                  <div className="rounded-xl border border-glass bg-glass-2/40 p-3 text-sm space-y-2">
                    {previewChannel === 'EMAIL' && (
                      <div>
                        <span className="text-glass-text-2 text-xs uppercase">Subject</span>
                        <p className="text-glass-text font-medium">{previewOut.subjectRendered}</p>
                      </div>
                    )}
                    <div>
                      <span className="text-glass-text-2 text-xs uppercase">Body</span>
                      <p className="text-glass-text whitespace-pre-wrap">{previewOut.bodyRendered}</p>
                    </div>
                  </div>
                )}
                <div className="border-t border-glass pt-3 space-y-2">
                  <p className="text-sm font-medium text-glass-text">Save as named variant</p>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[12rem]">
                      <label className="text-xs text-glass-text-2">Name</label>
                      <Input
                        value={variantName}
                        onChange={(e) => setVariantName(e.target.value)}
                        placeholder="e.g. Brand X — formal email"
                      />
                    </div>
                    <Button
                      type="button"
                      onClick={saveAsVariant}
                      disabled={!variantName.trim() || !resolvedOrgId}
                    >
                      Save variant
                    </Button>
                  </div>
                  {variantOptionsForChannel.length > 0 && (
                    <p className="text-xs text-glass-text-2">
                      Saved for this event/channel: {variantOptionsForChannel.map((v) => v.name).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
